import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { SurveyStatus } from '@ps/survey-kernel';
import {
  SurveySort,
  ChildSurveyFilter,
  SurveyTabStatus,
  CombineSurveyTrafficReportParams,
  SurveyStatusParam,
} from '@ps/survey-utils';

import { SurveyListActions } from './survey-list/survey-list.actions';
import { SurveyStateFilters, SurveyRequestContext } from './survey-state.model';
import { SurveyReferenceActions } from './reference/survey-reference.actions';
import { ManageSurveyActions } from './manage-survey/manage-survey.actions';

@Injectable({ providedIn: 'root' })
export class SurveyActionsFacade {
  private readonly _store = inject(Store);

  public init(context: SurveyRequestContext): void {
    this._store.dispatch(SurveyListActions.init({ context }));
  }

  public teardown(): void {
    this._store.dispatch(SurveyListActions.teardown());
  }

  public setFilters(filters: Partial<SurveyStateFilters>): void {
    this._store.dispatch(SurveyListActions.setFilters({ filters }));
  }

  public setTab(tabId: SurveyTabStatus, status: SurveyStatusParam): void {
    this._store.dispatch(SurveyListActions.setTab({ tabId, status }));
  }

  public cancelLoad(): void {
    this._store.dispatch(SurveyListActions.cancelLoad());
  }

  public setSort(sort: SurveySort): void {
    this._store.dispatch(SurveyListActions.setSort({ sort }));
  }

  public loadNextPage(): void {
    this._store.dispatch(SurveyListActions.loadNextPage());
  }

  public reload(): void {
    this._store.dispatch(SurveyListActions.reload());
  }

  public loadChildSurveys(parentId: number, filter: ChildSurveyFilter): void {
    this._store.dispatch(
      SurveyListActions.loadChildSurveys({ parentId, filter }),
    );
  }

  public updateSurveyStatus(surveyId: number, status: SurveyStatus): void {
    this._store.dispatch(
      SurveyListActions.updateSurveyStatus({ surveyId, status }),
    );
  }

  // Survey Preview actions
  public loadSurveyDetails(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSurveyDetails({ surveyId }));
  }

  public resetSurveyDetails(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyDetails());
  }

  public loadSurveySupplierCpis(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSupplierCpis({ surveyId }));
  }

  public updateCurrentCpi(surveyId: number, cpi: number): void {
    this._store.dispatch(
      ManageSurveyActions.updateCurrentCpi({ surveyId, cpi }),
    );
  }

  public loadSurveyQuotas(surveyId: number, page = 1): void {
    this._store.dispatch(
      ManageSurveyActions.loadSurveyQuotas({ surveyId, page }),
    );
  }

  public loadMoreQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadMoreQuotas({ surveyId }));
  }

  public loadAllQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadAllQuotas({ surveyId }));
  }

  public resetSurveyQuotas(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyQuotas());
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaLock({ surveyId, psQuotaId, locked }),
    );
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaCurrentTarget({
        surveyId,
        psQuotaId,
        currentTarget,
        updateSupplier,
      }),
    );
  }

  public loadTrafficChannels(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadTrafficChannels({ surveyId }));
  }

  public resetTrafficChannels(): void {
    this._store.dispatch(ManageSurveyActions.resetTrafficChannels());
  }

  public setTrafficChannelStatus(surveyId: number, status: SurveyStatus): void {
    this._store.dispatch(
      ManageSurveyActions.setTrafficChannelStatus({ surveyId, status }),
    );
  }

  // Reference actions
  public loadTemplates(hasImpersonatedBuyer: boolean, companyId: number): void {
    this._store.dispatch(
      SurveyReferenceActions.loadTemplates({ hasImpersonatedBuyer, companyId }),
    );
  }

  public loadBuyerManagers(companyId: number): void {
    this._store.dispatch(
      SurveyReferenceActions.loadBuyerManagers({ companyId }),
    );
  }

  public loadOperatorManagers(): void {
    this._store.dispatch(SurveyReferenceActions.loadOperatorManagers());
  }

  public loadTransactionStatuses(): void {
    this._store.dispatch(SurveyReferenceActions.loadTransactionStatuses());
  }

  public downloadCombinedSTReport(
    data: CombineSurveyTrafficReportParams,
  ): void {
    this._store.dispatch(
      SurveyReferenceActions.downloadCombinedSTReport({ ...data }),
    );
  }
}
